<script setup lang="ts">
/**
 * Aura AGM — Letter of Notification & Lodgement of Annual Return (to Aura Partners → ACRA).
 * 체크박스: 택1 그룹(company_type / company_activity / company_solvency / register 보관장소)은
 * select 변수 + 등식, 독립 선언(declare_*)은 boolean. 서명은 [[Signers]] (이사 2인 자동).
 */
import { inject, onMounted } from 'vue';
import CddVarField from '../cdd/CddVarField.vue';
import { useAgmFill, DOC_SCOPE } from '../cdd/useCddFill';
import './agmDoc.css';

const fill = useAgmFill();
const scope = inject(DOC_SCOPE, '');
const K = (k: string) => scope + k;

/** 택1 그룹 — 값 비교로 ☒/☐ */
function sel(key: string, value: string): boolean {
  return String(fill.values[K(key)] ?? '').trim() === value;
}
function pick(key: string, value: string) {
  fill.setValue(K(key), sel(key, value) ? '' : value);
}
/** 독립 boolean */
function on(key: string): boolean {
  const v = String(fill.values[K(key)] ?? '').trim().toLowerCase();
  return v === 'yes' || v === 'true' || v === '1';
}
function toggle(key: string) {
  fill.setValue(K(key), on(key) ? 'no' : 'yes');
}

const COMPANY_TYPES: Array<[string, string]> = [
  ['epc_shares', 'EXEMPT PRIVATE COMPANY LIMITED BY SHARES'],
  ['private_shares', 'PRIVATE COMPANY LIMITED BY SHARES'],
  ['public_guarantee', 'PUBLIC COMPANY LIMITED BY GUARANTEE'],
  ['public_shares', 'PUBLIC COMPANY LIMITED BY SHARES'],
  ['unlimited_epc', 'UNLIMITED EXEMPT PRIVATE COMPANY'],
  ['unlimited_private', 'UNLIMITED PRIVATE COMPANY'],
  ['unlimited_public', 'UNLIMITED PUBLIC COMPANY'],
];
const REGISTERS: Array<[string, string]> = [
  ['roc_location', 'Register of Controllers'],
  ['rond_location', 'Register of Nominee Directors'],
  ['rons_location', 'Register of Nominee Shareholders'],
];
const DECLARATIONS: Array<[string, string]> = [
  ['declare_dormant', 'Check this box to declare that the company is dormant for the entire financial period'],
];

// 기본값 시드 없이 두면 미설정 = 조건 거짓(빈 칸). 사용자가 직접 선택하게 둔다.
onMounted(() => {
  // Company Type Status (I) = Active 기본(AGM 표준). 빈 값일 때만 시드 → 생성 formData 에 확실히 실림.
  //   운영자가 Dormant 로 바꾸면 그 값 우선.
  if (!String(fill.values[K('company_activity')] ?? '').trim()) fill.setValue(K('company_activity'), 'active');
});
</script>

<template>
  <div class="agm-doc">
    <p class="doc-p">Date: <CddVarField field-key="notice_date" label="Notice Date" /></p>
    <p class="doc-p">Aura Partners (Singapore) Pte. Ltd.<br />1 Phillip Street, #08-00 Royal One Phillip, Singapore 048692</p>
    <p class="doc-p">Dear Sirs,</p>
    <div class="doc-title">
      NOTIFICATION AND LODGEMENT OF ANNUAL RETURN FOR THE FINANCIAL YEAR ENDED
      <CddVarField field-key="financial_year_end" label="Financial Year End" /> WITH ACRA
    </div>
    <p class="doc-p">I/We, the Director(s) of the Company hereby declare that the information lodged with ACRA (including the Annual Return for FYE <CddVarField field-key="financial_year_end" label="Financial Year End" />) is up-to-date, accurate and complete.</p>

    <div class="sec-title">Company Type During Financial Period Concerned (select one):</div>
    <div class="cb-grid">
      <div v-for="t in COMPANY_TYPES" :key="t[0]" class="cb-row doc-toggle" @click="pick('company_type', t[0])">
        <span class="cb-mark">{{ sel('company_type', t[0]) ? '☒' : '☐' }}</span><span>{{ t[1] }}</span>
      </div>
    </div>

    <div class="sec-title">Company Type Status (I):</div>
    <div class="cb-row doc-toggle" @click="pick('company_activity', 'active')"><span class="cb-mark">{{ sel('company_activity','active') ? '☒' : '☐' }}</span><span>Active</span></div>
    <div class="cb-row doc-toggle" @click="pick('company_activity', 'dormant')"><span class="cb-mark">{{ sel('company_activity','dormant') ? '☒' : '☐' }}</span><span>Dormant (for the entire financial period)</span></div>

    <div class="sec-title">Audit exemption declaration:</div>
    <div v-for="d in DECLARATIONS" :key="d[0]" class="cb-row doc-toggle" @click="toggle(d[0])">
      <span class="cb-mark">{{ on(d[0]) ? '☒' : '☐' }}</span><span>{{ d[1] }}</span>
    </div>

    <div class="sec-title">Where each register is kept (select per register):</div>
    <template v-for="r in REGISTERS" :key="r[0]">
      <div style="font-weight:600;margin-top:6px">{{ r[1] }}</div>
      <div class="cb-row doc-toggle" @click="pick(r[0], 'company')"><span class="cb-mark">{{ sel(r[0],'company') ? '☒' : '☐' }}</span><span>Registered office of the company</span></div>
      <div class="cb-row doc-toggle" @click="pick(r[0], 'csp')"><span class="cb-mark">{{ sel(r[0],'csp') ? '☒' : '☐' }}</span><span>Registered office of a corporate service provider appointed by the company</span></div>
    </template>

    <p class="doc-p" style="margin-top:12px">Yours faithfully,<br />Signed for and on behalf of <CddVarField field-key="company_name" label="Company Name" /></p>
    <div class="sign-line"></div>
    <p class="doc-p"><CddVarField field-key="director_name" label="Director Name" /><br />Director</p>
  </div>
</template>
